/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * FE_PTVang (ptvang@rsupartners.com)
 *
 * @description
 *
 * @created at Sat Dec 28 2024
 **/

import { WheelEvent, useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "@emotion/react";
import { useQuery } from "@tanstack/react-query";
import { TransformComponent, TransformWrapper, ReactZoomPanPinchRef } from "react-zoom-pan-pinch";
import { debounce } from "lodash-es";

import { EventHandler } from "@rfice/eventhandler";
import { _T } from "@rfice/language";

import { useFileManager, useCtrlPressed } from "@/frameworks/core/hooks";
import { useProperties } from "@/frameworks/libraries/properties";
import { Button, Column, Image, Row, Typography } from "@/frameworks/designStuff/components";
import useDocumentViewerStore from "../../stores/useDocumentViewerStore";
import useDocumentViewerTransform from "../hooks/useDocumentViewerTransform";
import {
	RightFilePreviewContainer,
	LeftFilePreviewContainer,
	RightFileWrap,
	wrapListThumbnail,
	wrapThumbnail
} from "./emotion";
import { LoadingFile } from "../../emotion";
import { MAX_SCALE } from "../constant";

import image172x102 from "@/assets/images/loadingFailed/thumbnail.png";
import image1280x720 from "@/assets/images/loadingFailed/content.png";

export default function FileViewer({ fileID, minimize }: { fileID: string; minimize?: boolean }) {
	const scale = useDocumentViewerStore((state) => state.scale);
	const prevScale = useDocumentViewerStore((state) => state.prevScale);
	const minScale = useDocumentViewerStore((state) => state.minScale);
	const page = useDocumentViewerStore((state) => state.page);
	const setPage = useDocumentViewerStore((state) => state.setPage);

	const [imageLoaded, setImageLoaded] = useState(false);
	const [isActiveThumbnail, setIsActiveThumbnail] = useState(true);

	const transformWrapperRef = useRef<ReactZoomPanPinchRef>(null);
	const isWheelZooming = useRef(false);

	const properties = useProperties();
	const { convertDocToImage, progressDocImage } = useFileManager();
	const { initializeViewer, updateViewerToCenter, handleWheel, updatePanning } =
		useDocumentViewerTransform(transformWrapperRef);
	const { isCtrlPressed } = useCtrlPressed();
	const theme = useTheme();

	/* Load File */
	const { data: convertResult } = useQuery({
		queryKey: ["convertDocToImage", fileID],
		queryFn: () => convertDocToImage({ fileId: fileID }),
		refetchOnWindowFocus: false
	});

	const { data: progress } = useQuery({
		queryKey: ["progressDocImage", convertResult?.docImageId],
		queryFn: () => progressDocImage({ id: convertResult!.docImageId }),
		enabled: Boolean(convertResult),
		refetchInterval: (data) => (!data || data.complete !== data.total ? 1000 : false),
		refetchOnWindowFocus: false
	});

	useEffect(() => {
		if (convertResult?.total) EventHandler.fire("$FileViewer::PageCountUpdated$", convertResult.total);
	}, [convertResult]);

	const thumbnailList = useMemo(() => {
		if (!convertResult || !progress) return [];
		return Array.from({ length: progress.total }, (_, index) =>
			index < progress.complete ? `/filebox/doc_image/${convertResult.docImageId}/thumbnail?no=${index + 1}` : ""
		);
	}, [progress, convertResult]);

	const imageSrcList = useMemo(() => {
		if (!convertResult || !progress) return [];
		return Array.from({ length: progress.total }, (_, index) =>
			index < progress.complete ? `/filebox/doc_image/${convertResult.docImageId}?no=${index + 1}` : ""
		);
	}, [progress, convertResult]);

	/* Initialize */
	useEffect(() => {
		if (imageLoaded) {
			const resolution = initializeViewer();
			EventHandler.fire("$Viewer::FileLoaded$", resolution, "transform-content");
		}
	}, [imageLoaded]);

	/* Resize Window */
	useEffect(() => {
		// resize가 될 때 문서를 화면의 센터로 맞춘다
		const handleResize = debounce(() => updateViewerToCenter(), 100);
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, [scale]);

	useEffect(() => {
		imageLoaded && updateViewerToCenter(scale, 0);
	}, [minimize]);

	useEffect(() => {
		// thumbnail이 열리고 닫힐 때 문서를 화면의 센터로 맞춘다
		updateViewerToCenter(scale, 0);
	}, [isActiveThumbnail]);

	/* Page */
	const getTargetPage = (type: "next" | "prev") => {
		if (type === "prev") return Math.max(page - 1, 0);
		if (type === "next") return Math.min(page + 1, (imageSrcList?.length ?? 1) - 1);
		return page;
	};

	// can update from shortcut, scroll, thumbnail
	const updatePage = (targetPage: number) => {
		setPage(targetPage);
		EventHandler.fire("$FileViewer::PageUpdated$", targetPage);
	};

	// reset page on return
	useEffect(() => {
		return () => setPage(0);
	}, []);

	/* Key Event */
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "ArrowLeft" || e.key === "ArrowUp") updatePage(getTargetPage("prev"));
			if (e.key === "ArrowRight" || e.key === "ArrowDown") updatePage(getTargetPage("next"));
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [imageSrcList, page]);

	/* Scroll Event */
	const handleScroll = (e: WheelEvent) => {
		if (isCtrlPressed) return;
		// 기본 scale일 경우 change page
		if (scale === minScale) updatePage(getTargetPage(e.deltaY < 0 ? "prev" : "next"));
		// zoom이 되어있는 경우 panning
		else updatePanning(e.deltaY);
	};

	const handleZoomStateChanged = (e: ReactZoomPanPinchRef, isStart: boolean) => {
		// zoom out인데 minScale일 경우 center
		if (!isStart && e.state.scale === minScale) updateViewerToCenter(e.state.scale);
		isWheelZooming.current = isStart;
	};

	/* Update Transform */
	useEffect(() => {
		if (isWheelZooming.current) return;
		const factor = Math.log(scale / prevScale);
		if (scale > prevScale) transformWrapperRef.current?.zoomIn(factor, 0);
		else transformWrapperRef.current?.zoomOut(-factor, 0);
	}, [scale]);

	if (!progress || progress.complete === 0)
		return (
			<LoadingFile typoSize="Head5_B" typoColor="neutral_00_100">
				{_T("로딩중")}
			</LoadingFile>
		);

	return (
		<>
			<LeftFilePreviewContainer>
				<Button.Icon
					name="LeftPanel24"
					filled="primary"
					size="Xlarge"
					fill="neutral_00_100"
					css={{ position: "absolute", top: "76px", left: "16px", zIndex: 1 }}
					onClick={() => setIsActiveThumbnail((prev) => !prev)}
				/>
				<Column
					gap={10}
					css={[wrapListThumbnail, !isActiveThumbnail && { width: 0, opacity: 0, margin: "auto" }]}
				>
					{thumbnailList?.map((image, index) => {
						return (
							<Row key={index}>
								<Typography
									typoSize="Body3_MD"
									typoColor="neutral_00_100"
									css={{
										width: "36px",
										paddingTop: "10px"
									}}
								>
									{index + 1}
								</Typography>
								<Column
									key={index}
									css={wrapThumbnail(page, index, theme.colors.accent_50_100)}
									onClick={() => updatePage(index)}
								>
									<Image
										src={`${properties.default.webDomain}${image}`}
										alt={image}
										errorSrc={image172x102}
									/>
								</Column>
							</Row>
						);
					})}
				</Column>
			</LeftFilePreviewContainer>
			<RightFilePreviewContainer>
				<RightFileWrap horizonAlign="center" onWheel={handleScroll}>
					<TransformWrapper
						ref={transformWrapperRef}
						minScale={minScale}
						maxScale={MAX_SCALE}
						wheel={{ step: 1, disabled: !isCtrlPressed }}
						pinch={{ disabled: false, step: 0.05 }}
						panning={{ disabled: false }}
						doubleClick={{ disabled: true }}
						limitToBounds={true}
						centerOnInit={true}
						onWheel={handleWheel}
						onWheelStart={(e) => handleZoomStateChanged(e, true)}
						onWheelStop={(e) => handleZoomStateChanged(e, false)}
						centerZoomedOut={isWheelZooming.current} // wheel zooming일때 center 되는 것을 방지
						zoomAnimation={{ disabled: true }} // ctrl + wheel시 라이브러리 자체에서 처리되는 규칙을 무시하기 위함
					>
						<TransformComponent
							contentStyle={{
								visibility: "visible",
								position: "absolute",
								opacity: imageLoaded ? 1 : 0,
								transition: "opacity 0.3s"
							}}
						>
							<div id="transform-content">
								<Image
									draggable={false}
									src={
										imageSrcList && imageSrcList[page]
											? `${properties.default.webDomain}${imageSrcList[page]}`
											: image1280x720
									}
									onLoad={() => {
										setImageLoaded(true);
										updateViewerToCenter(scale, 0);
									}}
									errorSrc={image1280x720}
									onError={(e) => console.log("#FileViewer#", "Failed to load image", e)}
								/>
							</div>
						</TransformComponent>
					</TransformWrapper>
				</RightFileWrap>
			</RightFilePreviewContainer>
		</>
	);
}
